<template>
  <aside
    v-if="!isEmptyMessages"
    class="chatbot-sidebar"
    :class="{ 'chatbot-sidebar--collapsed': collapsed }"
    aria-label="Messages overview"
  >
    <div class="chatbot-sidebar__sticky">
      <div class="chatbot-sidebar__top">
        <v-btn
          icon
          x-small
          type="button"
          class="chatbot-sidebar__toggle"
          :aria-label="collapsed ? 'Expand overview' : 'Collapse overview'"
          @click="$emit('toggle-collapse')"
        >
          <v-icon size="20">{{ collapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        </v-btn>
        <div v-if="!collapsed" class="chatbot-sidebar__title">Chats</div>
      </div>
      <div class="chatbot-sidebar__list">
        <v-expansion-panels v-model="openPanelIndexes" multiple flat class="chatbot-sidebar__panels">
          <v-expansion-panel v-for="group in groupedMessages" :key="group.key" class="chatbot-sidebar__exp-panel">
            <v-expansion-panel-header class="chatbot-sidebar__exp-header" hide-actions>
              <template #default="{ open }">
                <span class="chatbot-sidebar__exp-title-row">
                  <span class="chatbot-sidebar__group-title">{{ group.label }}</span>
                  <v-icon
                    class="chatbot-sidebar__exp-chevron"
                    :class="{ 'chatbot-sidebar__exp-chevron--open': open }"
                    size="18"
                  >
                    mdi-chevron-down
                  </v-icon>
                </span>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="chatbot-sidebar__exp-content">
              <ul class="chatbot-sidebar__group-list">
                <li v-for="item in group.items" :key="item.key" class="chatbot-sidebar__item">
                  <button
                    v-if="item.role === 'user'"
                    type="button"
                    class="chatbot-sidebar__line"
                    :class="{ 'chatbot-sidebar__line--user': item.role === 'user' }"
                    @click="$emit('select', item.baseKey)"
                  >
                    <span class="chatbot-sidebar__snippet">{{ item.text }}</span>
                    <span v-if="item.timeLabel" class="chatbot-sidebar__time">{{ item.timeLabel }}</span>
                  </button>
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </aside>
</template>

<script>
import moment from 'moment';

function timeValue(msg) {
  const v = msg && msg.createdAt ? Date.parse(msg.createdAt) : NaN;
  return Number.isFinite(v) ? v : 0;
}

function dayKeyFromMs(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default {
  name: 'OverviewChatMessages',
  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openPanelIndexes: [],
      overviewPanelsInit: false,
    };
  },
  watch: {
    groupedMessages: {
      immediate: true,
      handler(groups) {
        if (!groups || !groups.length) {
          this.openPanelIndexes = [];
          this.overviewPanelsInit = false;
          return;
        }
        if (!this.overviewPanelsInit) {
          const todayKey = dayKeyFromMs(Date.now());
          const idx = groups.findIndex(g => g.key === todayKey);
          this.openPanelIndexes = idx >= 0 ? [idx] : [0];
          this.overviewPanelsInit = true;
        }
      },
    },
  },
  computed: {
    isEmptyMessages() {
      return (this.messages || []).length === 0;
    },
    sortedMessagesByCreatedAt() {
      const list = Array.isArray(this.messages) ? this.messages : [];
      // Newest first (sidebar overview).
      return [...list].sort((a, b) => timeValue(b) - timeValue(a));
    },
    groupedMessages() {
      const groups = new Map();
      for (const msg of this.sortedMessagesByCreatedAt) {
        const t = timeValue(msg);
        const key = t ? dayKeyFromMs(t) : 'unknown';
        if (!groups.has(key)) {
          groups.set(key, { key, label: this.formatDayLabelFromKey(key, t), items: [] });
        }
        const items = this.toOverviewItems(msg);
        for (const it of items) groups.get(key).items.push(it);
      }

      // Ensure stable ordering inside each day group:
      // - Newest record first
      // - Within one record: user line above assistant line
      const values = Array.from(groups.values());
      for (const g of values) {
        g.items.sort((a, b) => {
          const msA = typeof a.ms === 'number' ? a.ms : 0;
          const msB = typeof b.ms === 'number' ? b.ms : 0;
          if (msA !== msB) return msB - msA;
          const orderA = typeof a.order === 'number' ? a.order : 0;
          const orderB = typeof b.order === 'number' ? b.order : 0;
          if (orderA !== orderB) return orderA - orderB;
          return String(a.key || '').localeCompare(String(b.key || ''));
        });
      }
      return values;
    },
  },
  methods: {
    msgKey(msg) {
      return (msg && (msg._key || msg._id)) || '';
    },
    toOverviewItems(msg) {
      const baseKey = this.msgKey(msg);
      const createdAt = msg && msg.createdAt ? msg.createdAt : null;
      const timeLabel = createdAt ? moment(createdAt).format('h:mm A') : '';
      const ms = timeValue(msg);

      const userText = msg && msg.userMessage != null ? String(msg.userMessage).trim() : '';
      const aiText = msg && msg.aiReply != null ? String(msg.aiReply).trim() : '';

      const out = [];
      if (aiText) {
        out.push({
          key: `${baseKey}::assistant`,
          baseKey,
          role: 'assistant',
          text: aiText,
          timeLabel,
          ms,
          order: 0,
        });
      }
      if (userText) {
        out.push({
          key: `${baseKey}::user`,
          baseKey,
          role: 'user',
          text: userText,
          timeLabel,
          ms,
          order: 1,
        });
      }

      if (out.length === 0) return [];
      return out;
    },
    formatDayLabelFromKey(key, ms) {
      if (key === 'unknown') return 'Unknown date';
      const now = new Date();
      const todayKey = dayKeyFromMs(now.getTime());
      if (key === todayKey) return 'Today';

      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const yesterdayKey = dayKeyFromMs(yesterday.getTime());
      if (key === yesterdayKey) return 'Yesterday';

      try {
        return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(
          new Date(ms),
        );
      } catch (e) {
        return key;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chatbot-sidebar {
  width: 280px;
  flex-shrink: 0;
  min-height: 0;
  height: 100%;
  border-right: 1px solid var(--v-border-base);
  background: var(--main-background);
  overflow: hidden;
  transition: width 160ms ease;
  will-change: width;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.chatbot-sidebar--collapsed {
  width: 48px;
}

.chatbot-sidebar__sticky {
  position: sticky;
  top: 0;
  height: 100%;
  max-height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 8px 10px 0 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chatbot-sidebar__top {
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 28px;
  margin-left: -6px;
}

.chatbot-sidebar__title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  user-select: none;
}

.chatbot-sidebar__toggle {
  flex-shrink: 0;
  height: 28px;
  width: 28px;
}

.chatbot-sidebar__empty {
  margin: 0;
  padding: 8px 6px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

.chatbot-sidebar--collapsed {
  .chatbot-sidebar__sticky {
    padding: 12px 6px;
    overflow: hidden;
  }

  .chatbot-sidebar__top {
    justify-content: center;
  }

  .chatbot-sidebar__list {
    display: none;
  }
}

.chatbot-sidebar__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;

  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
}

.chatbot-sidebar__group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chatbot-sidebar__exp-title-row {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 4px 4px 0 4px;

  &:hover {
    .chatbot-sidebar__group-title {
      color: var(--main-text-color);
    }
    i {
      color: var(--main-text-color) !important;
    }
  }
}

.chatbot-sidebar__exp-chevron {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.45) !important;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(-90deg);
}

.chatbot-sidebar__exp-chevron--open {
  transform: rotate(0deg);
}

.chatbot-sidebar__group-title {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.6);
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Expansion panels styled like the previous static section + group-title layout */
.chatbot-sidebar__panels {
  background: transparent !important;

  ::v-deep &.v-item-group {
    background: transparent;
  }

  ::v-deep .v-expansion-panel {
    background: transparent !important;
    margin-top: 0 !important;

    &::before {
      box-shadow: none !important;
    }

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  ::v-deep .v-expansion-panel--active > .v-expansion-panel-header {
    min-height: unset !important;
  }

  ::v-deep .chatbot-sidebar__exp-header.v-expansion-panel-header {
    padding: 0 !important;
    min-height: 0 !important;
    width: 100%;
    text-align: left;
    box-shadow: none;
    background: transparent !important;
    color: inherit;
    font: inherit;
    letter-spacing: inherit;
    text-transform: none;
    justify-content: flex-start;

    &::before {
      opacity: 0;
    }
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 4px 0 0 !important;
  }
}

.chatbot-sidebar__group-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chatbot-sidebar__item {
  margin: 0;
}

.chatbot-sidebar__line {
  position: relative;
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  color: var(--main-text-color);
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}

.chatbot-sidebar__snippet {
  flex: 1;
  min-width: 0;
  padding-right: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-sidebar__time {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1.2;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.6);
  padding-left: 10px;
  pointer-events: none;
}
</style>
